/*******************************************************************************
 * globals.js - Set up window.* globals BEFORE other modules load
 * 
 * This must be imported FIRST so that operations.js can access window.*
 ******************************************************************************/

import { ELEMENT_IDS, STORAGE_KEYS, API_ENDPOINTS, PAGE_SIZES, DEFAULT_PAGE_SIZE } from './constants.js';
import { getState, setState, updateState, resetState } from './state.js';
import { 
    getElement, 
    getGrid, 
    showElement, 
    hideElement, 
    setVisibility,
    showDialog, 
    hideDialog,
    formatCurrency,
    debounce,
    chunkArray,
    padTo2Digits,
    formatDate,
    parseBool,
    isCharNumber,
    findRowIndexById
} from './utils.js';
import { 
    getFromStorage, 
    saveToStorage, 
    removeFromStorage,
    getSelectionByOrgId,
    saveSelection,
    clearSelectionByOrgId
} from './storage.js';
import { logToServer, checkIfConnectedToXero, createAjax, postJson, fetchJson } from './api.js';
import * as dialogs from './dialogs.js';
import * as grid from './grid.js';

// Initialize state variables that need to be on window
window.markAsPaidResult = undefined;
window.disabledGridEditing = false;
window.newDataRecord = [];
window.storedData = [];

// Shared Organisation View state
window.sharedViewGroupId = 0;
window.sharedViewExportMode = 0;
window.isSharedView = false;

// Expose constants
window.ELEMENT_IDS = ELEMENT_IDS;
window.STORAGE_KEYS = STORAGE_KEYS;
window.API_ENDPOINTS = API_ENDPOINTS;
window.PAGE_SIZES = PAGE_SIZES;
window.DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;

// Expose utility functions
window.getElement = getElement;
window.getGrid = getGrid;
window.showElement = showElement;
window.hideElement = hideElement;
window.setVisibility = setVisibility;
window.showDialog = showDialog;
window.hideDialog = hideDialog;
window.formatCurrency = formatCurrency;
window.debounce = debounce;
window.chunkArray = chunkArray;
window.padTo2Digits = padTo2Digits;
window.formatDate = formatDate;
window.parseBool = parseBool;
window.isCharNumber = isCharNumber;
window.findRowIndexById = findRowIndexById;

// Expose storage functions
window.getFromStorage = getFromStorage;
window.saveToStorage = saveToStorage;
window.removeFromStorage = removeFromStorage;

// Expose API functions
window.logToServer = logToServer;
window.CheckIfConnectedToXero = checkIfConnectedToXero;

// Shared Organisation View handler
window.onSharedViewChange = function(selectEl) {
    var groupId = parseInt(selectEl.value) || 0;
    var selectedOption = selectEl.options[selectEl.selectedIndex];
    var exportMode = parseInt(selectedOption.getAttribute('data-export-mode')) || 0;

    window.sharedViewGroupId = groupId;
    window.sharedViewExportMode = exportMode;
    window.isSharedView = groupId > 0;

    var grid = getGrid();
    if (!grid) return;

    var orgCol = grid.getColumnByField('organisationName');

    var sharedReportBtn = document.getElementById('sharedBatchReportBtn');

    if (window.isSharedView) {
        if (orgCol) { orgCol.visible = true; grid.refreshColumns(); }
        if (sharedReportBtn) sharedReportBtn.style.display = '';
        window.loadSharedGroupBills(groupId);
    } else {
        if (orgCol) { orgCol.visible = false; grid.refreshColumns(); }
        if (sharedReportBtn) sharedReportBtn.style.display = 'none';
        if (typeof window.operations !== 'undefined' && typeof window.operations.onGetBills === 'function') {
            grid.showSpinner();
            window.operations.onGetBills(function() { grid.hideSpinner(); });
        }
    }
};

window.loadSharedGroupBills = function(groupId) {
    var grid = getGrid();
    if (!grid) return;
    grid.showSpinner();

    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) spinnerGlobal.style.visibility = "visible";

    var ajax = new ej.base.Ajax({
        url: API_ENDPOINTS.GET_SHARED_GROUP_BILLS,
        type: 'POST',
        data: JSON.stringify({ groupId: groupId }),
        contentType: 'application/json',
        accept: 'application/json'
    });
    ajax.send();
    ajax.onSuccess = function(data) {
        var obj = JSON.parse(data);
        if (obj.isError) {
            if (typeof window.operations !== 'undefined') window.operations.errorHandling(obj);
            grid.hideSpinner();
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            return;
        }

        var cDataSource = [];
        for (var i = 0; i < obj.invoices.length; i++) {
            var inv = obj.invoices[i];
            cDataSource.push({
                amountDue: inv.amountDue,
                amountPaid: inv.amountPaid,
                amountDueEditable: inv.amountDueEditable,
                amountCredited: inv.amountCredited,
                contact: inv.contact,
                contactName: inv.contactName,
                date: new Date(inv.date),
                plannedDate: new Date(inv.plannedDate),
                dueDate: new Date(inv.dueDate),
                emailAddress: inv.emailAddress,
                hasEmailAddress: inv.hasEmailAddress,
                invoiceID: inv.invoiceID,
                invoiceNumber: inv.invoiceNumber,
                paymentDate: inv.paymentDate,
                reference: inv.reference,
                sendDate: inv.sendDate,
                status: inv.status,
                sentToContact: inv.sentToContact,
                hasBankDetails: inv.hasBankDetails,
                CreditNoteID: inv.creditNoteID,
                ListOfCreditNote: inv.listOfCreditNote,
                Total: inv.total,
                ListOfPayments: inv.listOfPayments,
                organisationName: inv.organisationName || '',
                organisationTenantId: inv.organisationTenantId || ''
            });
        }

        window.newDataRecord = obj.invoices;
        grid.dataSource = cDataSource;
        grid.hideSpinner();
        if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";

        var totalEl = document.getElementById("total");
        var selectedBillsEl = document.getElementById("selectedBills");
        if (totalEl) totalEl.innerHTML = 0;
        if (selectedBillsEl) selectedBillsEl.innerHTML = 0;
        grid.clearCellSelection();
        grid.clearRowSelection();
        grid.clearSelection();
        if (grid.selectionModule) {
            grid.selectionModule.deSelectedData = [];
            grid.selectionModule.persistSelectedData = [];
        }
    };
    ajax.onFailure = function() {
        grid.hideSpinner();
        if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
        console.error('Failed to load shared group bills');
    };
};

window._sharedExportState = {};

window.initiateSharedExport = function(selectedInvoices, transactionDate) {
    if (!window.isSharedView || !window.sharedViewGroupId) return;

    var orgMap = {};
    selectedInvoices.forEach(function(inv) {
        var tid = inv.organisationTenantId || '';
        if (!tid) return;
        if (!orgMap[tid]) {
            orgMap[tid] = { tenantId: tid, organisationName: inv.organisationName || '', invoices: [] };
        }
        orgMap[tid].invoices.push({
            invoiceID: inv.invoiceID,
            amountDue: inv.amountDueEditable || inv.amountDue,
            contactId: inv.contact ? inv.contact.contactID : (inv.contactId || ''),
            contactName: inv.contactName || (inv.contact ? inv.contact.name : ''),
            bankAccountDetails: inv.bankDetails || inv.bankAccountDetails || '',
            invoiceNumber: inv.invoiceNumber || '',
            hasEmailAddress: inv.hasEmailAddress,
            hasBankDetails: inv.hasBankDetails
        });
    });

    var tenantIds = Object.keys(orgMap);
    if (tenantIds.length === 0) {
        alert('No invoices selected or missing organisation data.');
        return;
    }

    window._sharedExportState = {
        selectedInvoices: selectedInvoices,
        orgMap: orgMap,
        transactionDate: transactionDate,
        tenantIds: tenantIds
    };

    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) spinnerGlobal.style.visibility = "visible";

    $.ajax({
        url: API_ENDPOINTS.GET_BANK_ACCOUNTS_FOR_ORGS,
        type: 'POST',
        data: JSON.stringify(tenantIds),
        contentType: 'application/json',
        success: function(result) {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            if (!result.ok) {
                alert(result.message || 'Failed to load bank accounts.');
                return;
            }
            window._sharedExportState.orgsWithBanks = result.organisations;
            showSharedWizardStep2(result.organisations, orgMap, transactionDate);
        },
        error: function() {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            alert('Server error loading bank accounts.');
        }
    });
};

function hideAllSharedDialogs() {
    var ids = ['sharedExportDialog', 'sharedExportABADialog', 'sharedErrorListDialog'];
    ids.forEach(function(id) {
        var el = document.getElementById(id);
        if (el && el.ej2_instances && el.ej2_instances[0]) {
            el.ej2_instances[0].hide();
        }
    });
    $("#sharedExportContainer").css("visibility", "hidden");
    $("#sharedExportABAContainer").css("visibility", "hidden");
    $("#sharedErrorListContainer").css("visibility", "hidden");
}

function showSharedWizardStep2(orgsWithBanks, orgMap, transactionDate) {
    hideAllSharedDialogs();

    var sharedExportDlg = document.getElementById("sharedExportDialog").ej2_instances[0];

    document.getElementById("sharedProgressStep1").innerHTML = "<span style='background: #44C7F4;padding: 5px 10px;color: #fafafa;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>1</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -5px;white-space: normal;width: 80px;'>Select date</div>";
    document.getElementById("sharedProgressStep2").innerHTML = "<span style='background: #f5f5f5;padding: 5px 10px;color: #b7adad;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>2</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -10px;white-space: normal;width: 90px;'>Pay from</div>";
    document.getElementById("sharedProgressStep3").innerHTML = "<span style='background: #fff;padding: 5px 10px;color: #b7adad;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #c2c5c7;'>3</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -40px;white-space: normal;width: 90px;text-align: center;'>Export BatchPay File</div>";

    var bankHtml = '';
    orgsWithBanks.forEach(function(org, index) {
        bankHtml += '<p style="font-weight:600;margin-bottom:4px;">' + (org.organisationName || org.tenantId) + '</p>';
        bankHtml += '<input type="text" id="sharedBankDdl_' + index + '" />';
        if (index < orgsWithBanks.length - 1) bankHtml += '<br /><br />';
    });
    document.getElementById("sharedBankAccountsArea").innerHTML = bankHtml;

    var dlgHeader = document.getElementById("sharedExportDialog_dialog-header");
    if (dlgHeader) dlgHeader.classList.add("prompt-dlg-header-template");
    var dlgContent = document.getElementById("sharedExportDialog_dialog-content");
    if (dlgContent) dlgContent.classList.add("dlg-content-template");

    var regionCode = (typeof countryCode !== 'undefined') ? countryCode : '';
    var lodgementContainer = document.getElementById("sharedLodgementFromContainer");
    var sumContainer = document.getElementById("sharedChcbxIdAggregated");
    var lodgementInput = document.getElementById("sharedTxtLodgementReferenceFrom");

    if (regionCode === 'AU') {
        if (lodgementContainer) lodgementContainer.style.display = '';
        if (sumContainer) sumContainer.style.display = 'none';
        if (lodgementInput) lodgementInput.value = '';
    } else if (regionCode === 'NZ') {
        if (lodgementContainer) lodgementContainer.style.display = 'none';
        if (sumContainer) sumContainer.style.display = '';
        if (lodgementInput) lodgementInput.value = '';
    } else {
        if (lodgementContainer) lodgementContainer.style.display = 'none';
        if (sumContainer) sumContainer.style.display = 'none';
        if (lodgementInput) lodgementInput.value = '';
    }

    sharedExportDlg.show();

    orgsWithBanks.forEach(function(org, index) {
        var ddlEl = document.getElementById('sharedBankDdl_' + index);
        if (ddlEl && org.bankAccounts && org.bankAccounts.length > 0) {
            var ddl = new ej.dropdowns.DropDownList({
                dataSource: org.bankAccounts,
                fields: { text: 'name', value: 'bankAccountNumber' },
                placeholder: 'Select Bank Account',
                popupHeight: '220px',
                index: 0
            });
            ddl.appendTo(ddlEl);
        }
    });

    document.getElementById('btnSharedCancel').onclick = function() { hideAllSharedDialogs(); };
    document.getElementById('btnSharedNext').onclick = function() { onSharedWizardStep2Next(orgsWithBanks, orgMap, transactionDate); };
}

function onSharedWizardStep2Next(orgsWithBanks, orgMap, transactionDate) {
    var organisations = [];
    var hasMissingBank = false;

    orgsWithBanks.forEach(function(org, index) {
        var ddlEl = document.getElementById('sharedBankDdl_' + index);
        if (!ddlEl || !ddlEl.ej2_instances || !ddlEl.ej2_instances[0]) return;
        var ddl = ddlEl.ej2_instances[0];
        var selectedValue = ddl.value;
        if (!selectedValue) {
            hasMissingBank = true;
            return;
        }
        var selectedItem = null;
        if (org.bankAccounts) {
            selectedItem = org.bankAccounts.find(function(ba) { return ba.bankAccountNumber === selectedValue; });
        }
        organisations.push({
            tenantId: org.tenantId,
            organisationName: orgMap[org.tenantId] ? orgMap[org.tenantId].organisationName : org.organisationName,
            bankAccountNumber: selectedValue,
            bankAccountName: selectedItem ? selectedItem.name : '',
            bankAccountId: selectedItem ? selectedItem.accountID : '',
            invoices: orgMap[org.tenantId] ? orgMap[org.tenantId].invoices : []
        });
    });

    if (hasMissingBank) {
        alert('Please select a bank account for each organisation.');
        return;
    }

    window._sharedExportState.organisations = organisations;

    var allInvoices = [];
    organisations.forEach(function(org) {
        org.invoices.forEach(function(inv) {
            allInvoices.push({
                orgName: org.organisationName,
                contactName: inv.contactName || '',
                invoiceNumber: inv.invoiceNumber || '',
                hasEmail: inv.hasEmailAddress !== false,
                hasBankDetails: inv.hasBankDetails !== false
            });
        });
    });

    var errors = [];
    allInvoices.forEach(function(inv) {
        if (!inv.hasEmail) {
            errors.push({ orgName: inv.orgName, contactName: inv.contactName, invoiceNumber: inv.invoiceNumber, issue: 'No email address' });
        }
        if (!inv.hasBankDetails) {
            errors.push({ orgName: inv.orgName, contactName: inv.contactName, invoiceNumber: inv.invoiceNumber, issue: 'No bank details' });
        }
    });

    if (errors.length > 0) {
        showSharedWizardStep3Errors(transactionDate, errors, allInvoices.length);
    } else {
        doSharedExport(transactionDate);
    }
}

function showSharedWizardStep3Errors(transactionDate, errors, totalBills) {
    hideAllSharedDialogs();

    var sharedABADlg = document.getElementById("sharedExportABADialog").ej2_instances[0];

    document.getElementById("sharedABAProgressStep1").innerHTML = "<span style='background: #44C7F4;padding: 5px 10px;color: #fafafa;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>1</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -5px;white-space: normal;width: 80px;'>Select date</div>";
    document.getElementById("sharedABAProgressStep2").innerHTML = "<span style='background: #44C7F4;padding: 5px 10px;color: #fafafa;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>2</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -10px;white-space: normal;width: 90px;'>Pay from</div>";
    document.getElementById("sharedABAProgressStep3").innerHTML = "<span style='background: #f5f5f5;padding: 5px 10px;color: #b7adad;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>3</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -40px;white-space: normal;width: 90px;text-align: center;'>Export BatchPay File</div>";

    document.getElementById("sharedABAPrompt").innerHTML = 'You are about to generate a BatchPay file containing <b>' + totalBills + '</b> bills. The file however contains <b>' + errors.length + '</b> errors.';

    var dlgHeader = document.getElementById("sharedExportABADialog_dialog-header");
    if (dlgHeader) dlgHeader.classList.add("prompt-dlg-header-template");
    var dlgContent = document.getElementById("sharedExportABADialog_dialog-content");
    if (dlgContent) dlgContent.classList.add("dlg-content-template");

    sharedABADlg.show();

    document.getElementById('btnSharedABACancel').onclick = function() { hideAllSharedDialogs(); };
    document.getElementById('btnSharedShowIssues').onclick = function() { showSharedErrorList(errors, transactionDate); };
    document.getElementById('btnSharedABAOkay').onclick = function() { doSharedExportFiltered(transactionDate); };
}

function showSharedErrorList(errors, transactionDate) {
    hideAllSharedDialogs();

    var sharedErrorDlg = document.getElementById("sharedErrorListDialog").ej2_instances[0];

    var html = '';
    errors.forEach(function(err) {
        html += '<li style="margin-bottom:10px">[' + (err.orgName || '') + '] ' + (err.contactName || '') + ' - ' + (err.invoiceNumber || '') + ': <span style="color:#e74c3c;">' + err.issue + '</span></li>';
    });
    document.getElementById("sharedErrorListContent").innerHTML = html;

    var dlgHeader = document.getElementById("sharedErrorListDialog_dialog-header");
    if (dlgHeader) dlgHeader.classList.add("prompt-dlg-header-template");
    var dlgContent = document.getElementById("sharedErrorListDialog_dialog-content");
    if (dlgContent) dlgContent.classList.add("dlg-content-template");

    sharedErrorDlg.show();

    document.getElementById('btnSharedErrorBack').onclick = function() {
        var state = window._sharedExportState;
        var allInvoices = [];
        state.organisations.forEach(function(org) {
            org.invoices.forEach(function(inv) {
                allInvoices.push({ orgName: org.organisationName, contactName: inv.contactName || '', invoiceNumber: inv.invoiceNumber || '', hasEmail: inv.hasEmailAddress !== false, hasBankDetails: inv.hasBankDetails !== false });
            });
        });
        var errs = [];
        allInvoices.forEach(function(inv) {
            if (!inv.hasEmail) errs.push({ orgName: inv.orgName, contactName: inv.contactName, invoiceNumber: inv.invoiceNumber, issue: 'No email address' });
            if (!inv.hasBankDetails) errs.push({ orgName: inv.orgName, contactName: inv.contactName, invoiceNumber: inv.invoiceNumber, issue: 'No bank details' });
        });
        showSharedWizardStep3Errors(transactionDate, errs, allInvoices.length);
    };
    document.getElementById('btnSharedErrorExport').onclick = function() { doSharedExportFiltered(transactionDate); };
}

function doSharedExportFiltered(transactionDate) {
    var state = window._sharedExportState;
    var filteredOrgs = [];
    state.organisations.forEach(function(org) {
        var filtered = org.invoices.filter(function(inv) {
            return inv.hasBankDetails !== false;
        });
        if (filtered.length > 0) {
            filteredOrgs.push(Object.assign({}, org, { invoices: filtered }));
        }
    });
    if (filteredOrgs.length === 0) {
        alert('No invoices remain after excluding bills with missing bank details.');
        return;
    }
    window._sharedExportState.organisations = filteredOrgs;
    doSharedExport(transactionDate);
}

function doSharedExport(transactionDate) {
    var state = window._sharedExportState;

    var lodgementFromEl = document.getElementById("sharedTxtLodgementReferenceFrom");
    var lodgementFrom = lodgementFromEl ? lodgementFromEl.value : '';

    var sumPerContactChecked = true;
    var sumChkEl = document.getElementById("sharedSumInvoicesPerContact");
    if (sumChkEl && sumChkEl.ej2_instances && sumChkEl.ej2_instances[0]) {
        sumPerContactChecked = sumChkEl.ej2_instances[0].checked;
    }

    var payload = {
        groupId: window.sharedViewGroupId,
        transactionDate: transactionDate,
        batchReference: state.batchRef || '',
        apcaID: state.apcaId || '301500',
        lodgementReferenceFrom: lodgementFrom,
        sumInvoicesPerContact: sumPerContactChecked,
        organisations: state.organisations
    };

    hideAllSharedDialogs();

    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) spinnerGlobal.style.visibility = "visible";

    var xhr = new XMLHttpRequest();
    xhr.open('POST', API_ENDPOINTS.EXPORT_SHARED_GROUP, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.responseType = 'blob';
    xhr.onload = function() {
        if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
        var contentType = xhr.getResponseHeader('Content-Type') || '';
        if (xhr.status === 200 && contentType.indexOf('application/zip') !== -1) {
            var blob = xhr.response;
            var url = window.URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = 'batch_export_' + transactionDate.replace(/\//g, '') + '.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } else if (xhr.status === 200 && contentType.indexOf('application/json') !== -1) {
            var reader = new FileReader();
            reader.onload = function() {
                try {
                    var json = JSON.parse(reader.result);
                    alert(json.message || json.Message || 'Export returned an error.');
                } catch(e) {
                    alert('Export failed. Unexpected response.');
                }
            };
            reader.readAsText(xhr.response);
        } else {
            alert('Export failed. Please try again.');
        }
    };
    xhr.onerror = function() {
        if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
        alert('Export failed. Server error.');
    };
    xhr.send(JSON.stringify(payload));
};

window.generateSharedBatchReport = function() {
    if (!window.isSharedView || !window.sharedViewGroupId) {
        alert('Please select a shared organisation group first.');
        return;
    }

    var grid = getGrid();
    if (!grid) return;

    var selectedRecords = grid.getSelectedRecords();
    if (!selectedRecords || selectedRecords.length === 0) {
        alert('Please select bills to generate the report.');
        return;
    }

    var orgMap = {};
    selectedRecords.forEach(function(inv) {
        var tid = inv.organisationTenantId || '';
        if (!tid) return;
        if (!orgMap[tid]) {
            orgMap[tid] = {
                tenantId: tid,
                organisationName: inv.organisationName || '',
                bankAccountNumber: '',
                bankAccountName: '',
                invoices: []
            };
        }
        var fmtDate = function(d) {
            if (!d) return '';
            var dt = new Date(d);
            if (isNaN(dt.getTime())) return '';
            return dt.toISOString().split('T')[0];
        };

        orgMap[tid].invoices.push({
            invoiceID: inv.invoiceID || '',
            amountDue: inv.amountDueEditable || inv.amountDue || 0,
            contactId: inv.contact ? inv.contact.contactID : (inv.contactId || ''),
            contactName: inv.contactName || (inv.contact ? inv.contact.name : ''),
            bankAccountDetails: inv.bankDetails || inv.bankAccountDetails || '',
            invoiceNumber: inv.invoiceNumber || '',
            invoiceDate: fmtDate(inv.date),
            dueDate: fmtDate(inv.dueDate),
            dateIntoXero: fmtDate(inv.date)
        });
    });

    var organisations = Object.values(orgMap);
    if (organisations.length === 0) {
        alert('No valid organisation data found in selected bills.');
        return;
    }

    var payload = {
        groupId: window.sharedViewGroupId,
        transactionDate: new Date().toLocaleDateString('en-AU'),
        organisations: organisations
    };

    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) spinnerGlobal.style.visibility = "visible";

    $.ajax({
        url: API_ENDPOINTS.GENERATE_SHARED_BATCH_REPORT,
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify(payload),
        success: function(result) {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";

            if (!result || result.isError) {
                alert(result ? (result.message || 'Report generation failed.') : 'Empty response from server.');
                return;
            }

            var downloadPdf = confirm('Shared Batch Report generated successfully.\n\nClick OK to download PDF, or Cancel to download CSV.');
            if (downloadPdf) {
                window.location.href = '/Xero/DownloadPDF/' + result.fileNamePDF + '/' + result.filePathPDF;
            } else {
                window.location.href = '/Xero/DownloadCSV/' + result.fileNameCSV + '/' + result.filePathCSV;
            }
        },
        error: function(xhr, status, error) {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            alert('Error generating shared batch report: ' + (error || 'Unknown error'));
        }
    });
};

window.initiateSharedMarkAsPaid = function(selectedInvoices, transactionDate, bankAccountCode, bankAccountNumber, batchReferenceNo, countryCode) {
    if (!window.isSharedView || !window.sharedViewGroupId) return;

    var orgMap = {};
    selectedInvoices.forEach(function(inv) {
        var tid = inv.organisationTenantId || '';
        if (!tid) return;
        if (!orgMap[tid]) {
            orgMap[tid] = { tenantId: tid, organisationName: inv.organisationName || '', invoices: [] };
        }
        orgMap[tid].invoices.push(inv);
    });

    var organisations = Object.keys(orgMap).map(function(tid) {
        return {
            tenantId: tid,
            organisationName: orgMap[tid].organisationName,
            bankAccountNumber: bankAccountNumber,
            bankAccountCode: bankAccountCode,
            invoices: orgMap[tid].invoices
        };
    });

    var payload = {
        groupId: window.sharedViewGroupId,
        transactionDate: transactionDate,
        batchReferenceNo: batchReferenceNo,
        countryCode: countryCode,
        organisations: organisations
    };

    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) spinnerGlobal.style.visibility = "visible";

    $.ajax({
        url: API_ENDPOINTS.MARK_BILLS_AS_PAID_SHARED,
        type: 'POST',
        data: JSON.stringify(payload),
        contentType: 'application/json',
        success: function(result) {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            if (result.ok) {
                window.markAsPaidResult = result;
                var msg = result.errors && result.errors.length > 0
                    ? 'Completed with ' + result.errors.length + ' organisation error(s). Check details.'
                    : 'All bills have been marked as paid successfully.';
                alert(msg);
                if (window.sharedViewGroupId) {
                    window.loadSharedGroupBills(window.sharedViewGroupId);
                }
            } else {
                alert(result.message || 'Failed to mark bills as paid.');
            }
        },
        error: function() {
            if (spinnerGlobal) spinnerGlobal.style.visibility = "hidden";
            alert('Server error marking bills as paid.');
        }
    });
};

// Tenant not connected handler - shows dialog when Xero connection is lost
window.tenantNotConnected = function() {
    var progressStatus4 = document.getElementById("progressStatus4");
    var prog4 = document.getElementById("Prog4");
    if (progressStatus4) progressStatus4.style.display = "none";
    if (prog4) prog4.style.display = "none";

    var dialogEl = document.getElementById("promptInfoDialog");
    if (!dialogEl || !dialogEl.ej2_instances) return;
    
    var dialog = dialogEl.ej2_instances[0];
    var promptContent = document.getElementById("promptContent");
    if (promptContent) {
        promptContent.innerHTML = "Tenant is disconnected from Xero.";
    }

    var dlgHeader = document.getElementById("promptInfoDialog_dialog-header");
    if (dlgHeader) dlgHeader.classList.add("error-dlg-header-template");

    var dlgContent = document.getElementById("promptInfoDialog_dialog-content");
    if (dlgContent) dlgContent.classList.add("dlg-content-template");

    var btnPromptCancel = document.getElementById("btnPromptCancel");
    if (btnPromptCancel) btnPromptCancel.style.display = "none";

    var btnPromptOkay = document.getElementById("btnPromptOkay");
    if (btnPromptOkay) {
        btnPromptOkay.onclick = function() {
            if (typeof SwitchLocation === 'function') {
                SwitchLocation();
            } else {
                window.location.href = "/Authorization/Index";
            }
        };
    }

    dialog.show();
};

// Expose dialog before-open handlers
window.promptContainerMoreInfoForABABeforeOpen = dialogs.promptContainerMoreInfoForABABeforeOpen;
window.dlgContainerBeforeOpen = dialogs.dlgContainerBeforeOpen;
window.dlgViewErrorListBeforeOpen = dialogs.dlgViewErrorListBeforeOpen;
window.dlgFeedbackModalBeforeOpen = dialogs.dlgFeedbackModalBeforeOpen;
window.dlgMarkAsPaidConfirmationBeforeOpen = dialogs.dlgMarkAsPaidConfirmationBeforeOpen;
window.dlgMarkAsPaidViewErrorListBeforeOpen = dialogs.dlgMarkAsPaidViewErrorListBeforeOpen;
window.dlgContainerAfterExportBeforeOpen = dialogs.dlgContainerAfterExportBeforeOpen;
window.errorDlgContainerBeforeOpen = dialogs.errorDlgContainerBeforeOpen;
window.promptContainerBeforeOpen = dialogs.promptContainerBeforeOpen;
window.promptContainerBeforeOpen1 = dialogs.promptContainerBeforeOpen1;
window.promptDlgContainerBeforeOpen = dialogs.promptDlgContainerBeforeOpen;
window.promptDlgContainerReportBeforeOpen = dialogs.promptDlgContainerReportBeforeOpen;
window.promptDlgContainerUpdateRefBeforeOpen = dialogs.promptDlgContainerUpdateRefBeforeOpen;
window.promptDlgContainerTransactionDateBeforeOpen = dialogs.promptDlgContainerTransactionDateBeforeOpen;
window.promptDlgContainerTransactionDate2 = dialogs.promptDlgContainerTransactionDate2;
window.promptDlgProgressContainerBeforeOpen = dialogs.promptDlgProgressContainerBeforeOpen;
window.promptDlgProgressContainerReportBeforeOpen = dialogs.promptDlgProgressContainerReportBeforeOpen;
window.promptDlgContainerSendingRemittanceProgressBeforeOpen = dialogs.promptDlgContainerSendingRemittanceProgressBeforeOpen;
window.promptContainerForABABeforeOpen = dialogs.promptContainerForABABeforeOpen;
window.sharedExportContainerBeforeOpen = dialogs.sharedExportContainerBeforeOpen;
window.sharedExportABAContainerBeforeOpen = dialogs.sharedExportABAContainerBeforeOpen;
window.sharedErrorListContainerBeforeOpen = dialogs.sharedErrorListContainerBeforeOpen;
window.promptContainerForMarkAsPaidBeforeOpen = dialogs.promptContainerForMarkAsPaidBeforeOpen;
window.promptContainerExpireSubscriptionsBeforeOpen = dialogs.promptContainerExpireSubscriptionsBeforeOpen;
window.promptContainerWelcomeSubscriptionsBeforeOpen = dialogs.promptContainerWelcomeSubscriptionsBeforeOpen;
window.promptContainerActivateSubscriptionsBeforeOpen = dialogs.promptContainerActivateSubscriptionsBeforeOpen;

// Expose state proxies - define property helper that handles existing properties
function defineStateProperty(name) {
    try {
        if (name in window) {
            delete window[name];
        }
        Object.defineProperty(window, name, {
            get: () => getState(name),
            set: (v) => setState(name, v),
            configurable: true
        });
    } catch (e) {
        console.warn(`Could not define property ${name}:`, e.message);
    }
}

// Define all state properties
const stateProps = [
    'dialogObj', 'total', 'count', 'billsSelected', 'newDataRecord',
    'bankAccount', 'bankAccountName', 'bankAccountCode', 'bankAccountID',
    'orgName', 'totalRowCount', 'objSelectedBills', 'markAsPaidResult',
    'disabledGridEditing', 'selectedInvoiceWithNoBankDetails',
    'selectedInvoiceWithNoBankDetailsReportExport', 'subscriptionID',
    'bankOptionSelected', 'bankOption', 'isDbb', 'desc',
    'windowHeight', 'windowWidth', 'downloadInfo', 'storedData',
    'showLodgementReference', 'selectedPageSizes', 'isPaid', 'isFormatChecking',
    'currentPagerValue', 'selectedPageSize', 'txtBacsServiceUserNo',
    'txtNarrativeUserChoice', 'txtBatchReferenceNo', 'chksumInvoicesPerContact',
    'cellEditRowIndex', 'isTrialPeriodShow', 'table', 'elementIDName'
];

stateProps.forEach(defineStateProperty);

// Global flag variable for grid load tracking
window.flag = true;

// Export report dropdown handler
window.onSelect = function(args) {
    var selectedText = args.item.text;
    switch (selectedText) {
        case "Export to PDF":
            window.initiatePDFDownload();
            break;
        case "Export to CSV":
            window.initiateCSVDownload();
            break;
        default:
            console.log("Unknown item selected");
            break;
    }
};

// PDF download function
window.initiatePDFDownload = function() {
    var downloadInfo = getState('downloadInfo') || {};
    if (downloadInfo.fileNamePDF && downloadInfo.filePathPDF) {
        var downloadUrl = API_ENDPOINTS.DOWNLOAD_PDF + downloadInfo.fileNamePDF + "/" + downloadInfo.filePathPDF;
        window.initiateDownload(downloadUrl);
    } else {
        console.error("PDF download information not available.");
    }
};

// CSV download function
window.initiateCSVDownload = function() {
    var downloadInfo = getState('downloadInfo') || {};
    if (downloadInfo.fileNameCSV && downloadInfo.filePathCSV) {
        var downloadUrl = API_ENDPOINTS.DOWNLOAD_CSV + downloadInfo.fileNameCSV + "/" + downloadInfo.filePathCSV;
        window.initiateDownload(downloadUrl);
    } else {
        console.error("CSV download information not available.");
    }
};

// Generic download initiator
window.initiateDownload = function(downloadUrl) {
    var checkDownloadStatus = function() {
        var xhr = new XMLHttpRequest();
        xhr.open("HEAD", downloadUrl, true);
        xhr.onreadystatechange = function() {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    var existingSelection = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_DATA)) || [];
                    var subscriptionID = getState('subscriptionID');
                    var filteredSelection = existingSelection.filter(function(item) {
                        return item.orgId !== undefined && item.orgId !== subscriptionID;
                    });
                    localStorage.setItem(STORAGE_KEYS.SELECTED_DATA, JSON.stringify(filteredSelection));
                    window.location.href = downloadUrl;
                }
            }
        };
        xhr.send();
    };
    checkDownloadStatus();
};

// Date picker change handlers
window.onDtChange = function(args) {
    var msgEl = getElement('msgTransactionDate');
    if (msgEl) {
        msgEl.style.display = args.value === null ? "block" : "none";
    }
};

window.onDtChangeMp = function(args) {
    var msgEl = getElement('msgTransactionDateMp');
    if (msgEl) {
        msgEl.style.display = args.value === null ? "block" : "none";
    }
};

// Menu select handler
window.onMenuSelect = function(args) {
    if (args.item.text === "Update Reference") {
        window.promptUpdateRef && window.promptUpdateRef();
    }
};

// Export file click handlers - trigger toolbar button clicks
window.exportAbaFile = function() {
    document.getElementById("Export").click();
};

window.exportKundupeiFile = function() {
    document.getElementById("ExportKundupei").click();
};

window.exportGiroFile = function() {
    document.getElementById("ExportGiro").click();
};

window.exportCSV = function() {
    document.getElementById("ExportCSV").click();
};

window.exportBacsFile = function() {
    document.getElementById("ExportToBacs").click();
};

window.markAsPaid = function() {
    $('#listReportBug').attr('disabled', 'disabled');
    document.getElementById("spinnerGlobal").style.visibility = "visible";
    var grid = document.getElementById("Grid").ej2_instances[0];
    grid.showSpinner();
    grid.refresh();

    let payload = {
        page: 1,
    };
    if (CheckIfConnectedToXero()) {
        var ajax = new ej.base.Ajax({
            url: API_ENDPOINTS.FILTER_BILLS,
            type: 'POST',
            data: JSON.stringify(payload),
            contentType: 'application/json',
            accept: 'application/json'
        });
        ajax.send();
        ajax.onSuccess = function (data) {
            document.getElementById("spinnerGlobal").style.visibility = "visible";
            var obj = JSON.parse(data);
            newDataRecord = obj.invoices;
            if (obj.isError) {
                if (window.operations && window.operations.errorHandling) {
                    window.operations.errorHandling(obj);
                }
                return;
            }
            grid.showSpinner();
            grid.refresh();

            document.getElementById("MarkAsPaid").click();
            grid.hideSpinner();
            $('#listReportBug').removeAttr('disabled');
        };
        ajax.onFailure = function(e) {
            console.error('Failed to refresh data:', e);
            grid.hideSpinner();
            document.getElementById("spinnerGlobal").style.visibility = "hidden";
        };
    } else {
        tenantNotConnected();
    }
};

window.sendRemittances = function() {
    document.getElementById("SendRemittances").click();
};

window.exportBatchPaymentReport = function() {
    document.getElementById("ExportBatchPaymentReport").click();
};

// Prompt dialog functions
window.promptTransactionDate = function() {
    document.getElementById("messageTransactionDate").style.display = "none";
    var d = new Date();
    var dateTransaction = document.getElementById("dateTransaction").ej2_instances[0];
    dateTransaction.value = d;

    var dialog = document.getElementById("promptInfoDialogTransactionDate").ej2_instances[0];
    document.getElementById("promptContent").innerHTML = "";
  
    var dlgHeader = document.getElementById("promptInfoDialogTransactionDate_dialog-header");
    dlgHeader.classList.add("prompt-dlg-header-template");

    var dlgContent = document.getElementById("promptInfoDialogTransactionDate_dialog-content");
    dlgContent.classList.add("dlg-content-template");
    dialog.show();
};

window.promptTransactionDate2 = function() {
    document.getElementById("messageTransactionDateMp").style.display = "none";
    var d = new Date();
    var dateTransaction = document.getElementById("dateTransaction2").ej2_instances[0];
    dateTransaction.value = d;

    var dialog = document.getElementById("promptInfoDialogTransactionDate2").ej2_instances[0];
    document.getElementById("promptContent").innerHTML = "";

    var dlgHeader = document.getElementById("promptInfoDialogTransactionDate2_dialog-header");
    dlgHeader.classList.add("prompt-dlg-header-template");

    var dlgContent = document.getElementById("promptInfoDialogTransactionDate2_dialog-content");
    dlgContent.classList.add("dlg-content-template");
    dialog.show();
};

window.promptdialogPaynow = function() {
    var promptDialog = document.getElementById("promptDialog").ej2_instances[0];

    var dlgHeader = document.getElementById("promptDialog_dialog-header");
    dlgHeader.classList.add("prompt-dlg-header-template");

    var dlgContent = document.getElementById("promptDialog_dialog-content");
    dlgContent.classList.add("dlg-content-template");

    document.getElementById("errorContentBank").innerHTML = '';
    promptDialog.show();
};

window.promptUpdateRef = function() {
    var grid = document.getElementById("Grid").ej2_instances[0];

    if (grid.getSelectedRecords().length === 0) {
        var message = "* Please select bills to update. <br/><br/>";

        var dialog = document.getElementById("dialog").ej2_instances[0];
        document.getElementById("message").innerHTML = message;

        var dlgHeader = document.getElementById("dialog_dialog-header");
        var dlgContent = document.getElementById("dialog_dialog-content");

        dlgHeader.classList.add("prompt-dlg-header-template");
        dlgContent.classList.add("dlg-content-template");

        dialog.show();
        grid.hideSpinner();
    } else {
        document.getElementById("txtRef").value = "";
        var dialog = document.getElementById("promptInfoDialogUpdateRef").ej2_instances[0];
        var dlgHeader = document.getElementById("promptInfoDialogUpdateRef_dialog-header");
        dlgHeader.classList.add("prompt-dlg-header-template");

        var dlgContent = document.getElementById("promptInfoDialogUpdateRef_dialog-content");
        dlgContent.classList.add("dlg-content-template");
        dialog.show();
        document.getElementById("btnPromptOkayUpdateRef").onclick = function (e) {
            var refNewValue = document.getElementById("txtRef").value;
            var records = grid.getSelectedRecords();
            for (var i = 0; i < records.length; i++) {
                grid.updateCell(grid.getRowIndexByPrimaryKey(records[i].invoiceID), "invoiceNumber", refNewValue);
            }
            grid.editModule.batchSave();
            dialog.hide();
        };

        document.getElementById("btnPromptCancelUpdateRef").onclick = function (e) {
            dialog.hide();
        };
    }
};

window.promptBankDetails = function(selectedInvoices) {
    var contactWithNoEmail = "";
    selectedInvoices.filter(function(i) { return i.hasEmailAddress == false; }).forEach(function(i) { contactWithNoEmail += ", " + i.contactName; });

    var dialog = document.getElementById("promptInfoDialog").ej2_instances[0];
    document.getElementById("promptContent").innerHTML = "Contacts with no email address:" + contactWithNoEmail.replaceAll(",", "<br/>") + "<br />  <br />  Do you want to continue transaction?";

    var dlgHeader = document.getElementById("promptInfoDialog_dialog-header");
    dlgHeader.classList.add("prompt-dlg-header-template");

    var dlgContent = document.getElementById("promptInfoDialog_dialog-content");
    dlgContent.classList.add("dlg-content-template");
    dialog.show();
};

window.cancelPayment = function() {
    var div = document.getElementById("wizardForm");
    if (div.style.display === "none") {
        div.style.display = "block";
    } else {
        div.style.display = "none";
    }
};

window.cellSaved = function(e) {
    var grid = document.getElementById("Grid").ej2_instances[0];
    grid.editModule.batchSave();
};

window.keyDownHandler = function(e) {
    console.log(e.keyCode);
};

// Grid selection functions for persisting selection across refresh
window.gridSelection = {
    getSelection: function(orgId) {
        try {
            const storedData = localStorage.getItem(STORAGE_KEYS.SELECTED_DATA);
            const parsedData = storedData ? JSON.parse(storedData) : [];
            return parsedData.filter(function(item) { return item.orgId === orgId; });
        } catch (error) {
            console.error('Error retrieving selection:', error);
            return [];
        }
    },
    saveSelection: function(selectedData) {
        try {
            // Retrieve existing selection from localStorage (if any)
            var existingSelection = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_DATA)) || [];

            // Create a map of existing invoiceIDs for quick lookup
            var existingIds = new Set(existingSelection.map(function(item) { return item.invoiceID; }));
            
            // Filter out duplicates from selectedData (items already in localStorage)
            var newSelection = selectedData.filter(function(item) {
                return !existingIds.has(item.invoiceID);
            });

            // Append the new selectedData (excluding duplicates) to the existing selection
            var updatedSelection = existingSelection.concat(newSelection);
            
            // Remove any duplicates that might exist within the updatedSelection itself
            var seen = new Set();
            updatedSelection = updatedSelection.filter(function(item) {
                if (seen.has(item.invoiceID)) {
                    return false;
                }
                seen.add(item.invoiceID);
                return true;
            });

            // Save the updated selection to localStorage
            localStorage.setItem(STORAGE_KEYS.SELECTED_DATA, JSON.stringify(updatedSelection));
        } catch (e) {
            console.error("Error saving to localStorage", e);
        }
    }
};

// Also expose as standalone functions for backward compatibility
window.gridSelectionGetSelection = window.gridSelection.getSelection;
window.gridSelectionSaveSelection = window.gridSelection.saveSelection;

window.getSubscriptionIDFromExpireOrganization = function(expireOrganization) {
    if (expireOrganization && expireOrganization.length > 0) {
        const content = expireOrganization;
        const dataArray = content.split(",");
        const splitCode = dataArray.map(function(item) { return item.split(":"); });
        return splitCode.length > 0 ? splitCode[0][2] : null;
    }
    return null;
};

// Date formatting functions
window.padTo2Digits = function(num) {
    return num.toString().padStart(2, '0');
};

window.formatDate = function(date) {
    return [
        window.padTo2Digits(date.getMonth() + 1),
        window.padTo2Digits(date.getDate()),
        date.getFullYear(),
    ].join('/');
};

// Progress update function
window.updateProgress = function(chunksProcessed, totalChunks) {
    var progressPercentage = (chunksProcessed / totalChunks) * 100;
    console.log("Progress: " + progressPercentage + "%");
    var progressBar = document.getElementById("linear").ej2_instances[0];
    progressBar.value = progressPercentage;
};

window.displayErrors = function(errorsEncountered) {
    console.log("Errors encountered while processing chunks:");
    errorsEncountered.forEach(function(err) {
        console.log("Chunk " + (err.chunkIndex + 1) + ":", err.error);
    });
};

// Global variable to store the last error message for display
window.lastMarkAsPaidError = null;

// Post mark as paid request to server
window.postMarkAsPaid = async function(payload, promptDialog1, dialogProgress) {
    try {
        var resultMarkAsPaid = await $.ajax({
            url: API_ENDPOINTS.MARK_BILLS_PAID,
            type: 'POST',
            data: JSON.stringify(payload),
            beforeSend: function() {
                promptDialog1.hide();
            },
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                'Accept': 'application/json; charset=utf-8'
            }
        });
         
        if (resultMarkAsPaid.isError) {
            // Extract descriptive error message from the response
            var errorMessage = window.extractErrorMessage(resultMarkAsPaid);
            window.lastMarkAsPaidError = errorMessage;
            
            window.logToServer("error", "Mark as paid API returned error", { 
                error: errorMessage,
                invoiceCount: payload.selectedInvoices ? payload.selectedInvoices.length : 0 
            });
            
            // Display error inline in the progress dialog instead of hiding it
            document.getElementById("loadingProcess").style.visibility = "hidden";
            document.getElementById("SuccessStatus").style.visibility = "visible";
            document.getElementById("SuccessStatus").style.display = "initial";
            document.getElementById("SuccessStatus").innerHTML = window.getErrorDisplayHtml(errorMessage);
            
            // Show the OK button so user can close the dialog
            document.getElementById("btnPromptOkayMarkAsPaid").style.visibility = "visible";
            document.getElementById("btnPromptOkayMarkAsPaid").style.display = "initial";
            document.getElementById("btnPromptOkayMarkAsPaid").onclick = function(e) {
                dialogProgress.hide();
            };
            
            document.getElementById("Grid").ej2_instances[0].hideSpinner();
            document.getElementById("step1").ej2_instances[0].disabled = true;
            document.getElementById("step2").ej2_instances[0].disabled = true;
            
            // Return false to signal error without throwing (avoids triggering catch as transport failure)
            return false;
        } else {
            // Clear any previous error on success
            window.lastMarkAsPaidError = null;
            window.disabledGridEditing = true;
            
            // Helper to get payments array from various response formats
            var getPaymentsArray = function(response) {
                if (response && response.invoices) {
                    if (response.invoices._Payments) return response.invoices._Payments;
                    if (response.invoices.payments) return response.invoices.payments;
                    if (response.invoices.result && response.invoices.result._Payments) return response.invoices.result._Payments;
                }
                return [];
            };
            
            // Normalize response to always have consistent payments property
            var normalizePayments = function(response) {
                if (response && response.invoices) {
                    var payments = getPaymentsArray(response);
                    response.invoices.payments = payments;
                    response.invoices._Payments = payments;
                }
                return response;
            };
            
            if (window.markAsPaidResult === undefined) {
                window.markAsPaidResult = normalizePayments(resultMarkAsPaid);
            } else {
                // Concatenate payments from new result into existing
                var existingPayments = getPaymentsArray(window.markAsPaidResult);
                var newPayments = getPaymentsArray(resultMarkAsPaid);
                var allPayments = existingPayments.concat(newPayments);
                
                // Store in both properties for compatibility
                window.markAsPaidResult.invoices._Payments = allPayments;
                window.markAsPaidResult.invoices.payments = allPayments;
            }
        }

        return true;
    } catch (error) {
        // Store error message for failed AJAX requests
        window.lastMarkAsPaidError = error.message || error.statusText || "Network error - please check your connection";
        window.logToServer("error", "Mark as paid AJAX request failed", { 
            error: error.message || error.statusText || "Unknown error",
            status: error.status 
        });
        throw error;
    }
};

// Helper function to generate error display HTML with descriptive message
window.getErrorDisplayHtml = function(errorMessage) {
    // Truncate very long error messages for display (keep full message in title)
    var displayMessage = errorMessage;
    if (displayMessage && displayMessage.length > 150) {
        displayMessage = displayMessage.substring(0, 147) + '...';
    }
    return '<div class="msg_success" style="background: #efd1ce !important; padding: 15px; border: 1px solid #ebc0bc;border-radius: 5px;font-size: 15px;color: #8f1b0f !important;"> <div style="margin-top: 0px;margin-right: 8px;"> <span class="e-icons e-close" title="Error" style="color: #cd0f39;font-size: 8px;border: 1px solid;border-radius: 50%;padding: 5px;"></span></div> <div> <span style="color: rgb(207 24 24 / 87%);" title="' + (errorMessage || '').replace(/"/g, '&quot;') + '"> ' + (displayMessage || 'An error occurred') + ' </span></div></div>';
};

// Helper function to extract descriptive error messages from various Xero response formats
window.extractErrorMessage = function(response) {
    try {
        // Format 1: Elements array with per-invoice validation errors
        if (response.Elements && Array.isArray(response.Elements)) {
            var errors = [];
            response.Elements.forEach(function(element) {
                if (element.ValidationErrors && Array.isArray(element.ValidationErrors)) {
                    element.ValidationErrors.forEach(function(validationError) {
                        if (validationError.Message) {
                            errors.push(validationError.Message);
                        }
                    });
                }
            });
            if (errors.length > 0) {
                return errors.join('; ');
            }
        }
        
        // Format 2: Root-level ValidationErrors (batch-level errors)
        if (response.ValidationErrors && Array.isArray(response.ValidationErrors)) {
            var errors = [];
            response.ValidationErrors.forEach(function(validationError) {
                if (validationError.Message) {
                    errors.push(validationError.Message);
                }
            });
            if (errors.length > 0) {
                return errors.join('; ');
            }
        }
        
        // Format 3: invoices.validationErrors nested structure
        if (response.invoices && response.invoices.validationErrors && Array.isArray(response.invoices.validationErrors)) {
            var errors = [];
            response.invoices.validationErrors.forEach(function(validationError) {
                if (validationError.Message) {
                    errors.push(validationError.Message);
                }
            });
            if (errors.length > 0) {
                return errors.join('; ');
            }
        }
        
        // Format 4: Direct errorMessage field
        if (response.errorMessage) {
            return response.errorMessage;
        }
        
        // Format 5: Error in invoices.result with exception
        if (response.invoices && response.invoices.result && response.invoices.result.exception) {
            return response.invoices.result.exception;
        }
        
        // Format 6: innerMessage contains detailed Xero validation errors (PRIORITY over generic message)
        if (response.innerMessage) {
            // InnerMessage may be a JSON string containing validation details
            try {
                var innerParsed = typeof response.innerMessage === 'string' ? JSON.parse(response.innerMessage) : response.innerMessage;
                if (innerParsed.Message) {
                    return innerParsed.Message;
                }
                if (innerParsed.Detail) {
                    return innerParsed.Detail;
                }
                // If it's a string description of the error
                if (typeof innerParsed === 'string') {
                    return innerParsed;
                }
            } catch (e) {
                // InnerMessage is a plain string, use it directly
                return response.innerMessage;
            }
        }
        
        // Format 7: JsonExceptionFilter ErrorContent field
        if (response.errorContent) {
            try {
                var errorParsed = typeof response.errorContent === 'string' ? JSON.parse(response.errorContent) : response.errorContent;
                if (errorParsed.Message) {
                    return errorParsed.Message;
                }
                if (errorParsed.Detail) {
                    return errorParsed.Detail;
                }
            } catch (e) {
                return response.errorContent;
            }
        }
        
        // Format 8: PascalCase variants from C# JsonResult (InnerMessage, ErrorContent)
        if (response.InnerMessage) {
            try {
                var innerParsed = typeof response.InnerMessage === 'string' ? JSON.parse(response.InnerMessage) : response.InnerMessage;
                if (innerParsed.Message) {
                    return innerParsed.Message;
                }
                if (innerParsed.Detail) {
                    return innerParsed.Detail;
                }
            } catch (e) {
                return response.InnerMessage;
            }
        }
        
        if (response.ErrorContent) {
            try {
                var errorParsed = typeof response.ErrorContent === 'string' ? JSON.parse(response.ErrorContent) : response.ErrorContent;
                if (errorParsed.Message) {
                    return errorParsed.Message;
                }
            } catch (e) {
                return response.ErrorContent;
            }
        }
        
        // Format 9: Fallback to generic message field (lower priority than innerMessage)
        if (response.message) {
            return response.message;
        }
        
        // Final fallback to generic message
        return "An error occurred while processing the payment";
    } catch (e) {
        console.error("Error extracting error message:", e);
        return "An error occurred while processing the payment";
    }
};

// Process invoices in chunks
window.processInvoices = async function(invoicesChunked, bankAccount, bankAccountCode, dateTransaction, BatchReferenceNo, countryCode, promptDialog1, dialogProgress) {
    var chunksProcessed = 0;
    var totalChunks = invoicesChunked.length;
    var errorsEncountered = [];

    for (var chunkIndex = 0; chunkIndex < invoicesChunked.length; chunkIndex++) {
        var chunk = invoicesChunked[chunkIndex];
        console.log("Processing chunk: " + (chunkIndex + 1));

        var payload = {
            bankAccountNumber: bankAccount,
            bankAccountCode: bankAccountCode,
            invoices: chunk,
            TransactionDate: window.formatDate(dateTransaction.value),
            BatchReferenceNo: BatchReferenceNo,
            CountryCode: countryCode
        };

        try {
            var result = await window.postMarkAsPaid(payload, promptDialog1, dialogProgress);
            
            // If postMarkAsPaid returns false, an error was displayed inline - stop processing
            if (result === false) {
                console.log("Mark as paid returned validation error - stopping chunk processing");
                return; // Exit early, error already shown in dialog
            }
        } catch (error) {
            console.log("Error processing chunk " + (chunkIndex + 1) + ":", error.responseText);

            var gridInstance = document.getElementById("Grid").ej2_instances[0];
            var data = gridInstance.dataSource;

            chunk.forEach(function(invoice) {
                var rowIndex = data.findIndex(function(item) { return item.invoiceID === invoice.invoiceID; });
                if (rowIndex !== -1) {
                    data[rowIndex].status = "Failed";
                }
            });

            gridInstance.refresh();

            errorsEncountered.push({
                chunkIndex: chunkIndex,
                error: error.responseText
            });
        }

        chunksProcessed++;
        window.updateProgress(chunksProcessed, totalChunks);

        // Handle last chunk
        if (chunkIndex === totalChunks - 1) {
            console.log("This is the last chunk!");
            
            // Helper to get payments array from various response formats (defensive fallback)
            var getPaymentsFromResult = function() {
                if (window.markAsPaidResult && window.markAsPaidResult.invoices) {
                    if (window.markAsPaidResult.invoices._Payments) return window.markAsPaidResult.invoices._Payments;
                    if (window.markAsPaidResult.invoices.payments) return window.markAsPaidResult.invoices.payments;
                    if (window.markAsPaidResult.invoices.result && window.markAsPaidResult.invoices.result._Payments) {
                        return window.markAsPaidResult.invoices.result._Payments;
                    }
                }
                return [];
            };
            
            var paymentsArray = getPaymentsFromResult();
            console.log("Last chunk - markAsPaidResult:", window.markAsPaidResult);
            console.log("Last chunk - paymentsArray length:", paymentsArray.length);
            
            // Primary success indicator: isError === false from the API response
            // Secondary: payments array has entries
            var isSuccess = window.markAsPaidResult && 
                            window.markAsPaidResult.isError === false;
            var hasPayments = paymentsArray.length >= 1;
            
            console.log("Last chunk - isSuccess:", isSuccess, "hasPayments:", hasPayments);
            
            // Success when API explicitly returns isError: false (OR when payments exist and no error flag)
            if (window.markAsPaidResult !== undefined && window.markAsPaidResult !== null && 
                (isSuccess || (hasPayments && window.markAsPaidResult.isError !== true))) {
                
                console.log("SUCCESS PATH: Payments found, showing success UI");

                document.getElementById("step1").ej2_instances[0].disabled = true;
                document.getElementById("step2").ej2_instances[0].disabled = true;
                document.getElementById("step3").ej2_instances[0].disabled = false;
                document.getElementById("step4").ej2_instances[0].disabled = false;

                document.getElementById("btnPromptOkayMarkAsPaid").style.visibility = "visible";
                document.getElementById("btnPromptOkayMarkAsPaid").style.display = "initial";
                document.getElementById("loadingProcess").style.visibility = "hidden";
                document.getElementById("SuccessStatus").style.visibility = "visible";
                document.getElementById("SuccessStatus").style.display = "initial";
                
                // Show success message (not error)
                document.getElementById('SuccessStatus').innerHTML = '<div class="msg_success" style="background: #d4edda !important; padding: 15px; border: 1px solid #c3e6cb;border-radius: 5px;font-size: 15px;color: #155724 !important;"> <div style="margin-top: 0px;margin-right: 8px;"> <span class="e-icons e-check" title="Success" style="color: #28a745;font-size: 8px;border: 1px solid;border-radius: 50%;padding: 5px;"></span></div> <div> <span style="color: #155724;"> Payment marked successfully in Xero </span></div></div>';

                document.getElementById("progressStatusOnly").innerHTML = "<span style='background: #44C7F4;padding: 5px 10px;color: #fafafa;font-size: 13px;font-weight: 500;border-radius: 50%;border: 3px solid #44C7F4;'>3</span> <span></span><div style='z-index: 9999;position: absolute;margin-top: 10px;margin-left: -25px;white-space: normal;width: 90px;'><div style='z-index: 9999;position: absolute;white-space: normal;width: 90px;'>Update Xero</div>";

                document.getElementById("btnPromptOkayMarkAsPaid").onclick = function(e) {
                    dialogProgress.hide();
                    // Simply close dialog and update grid - window.markAsPaidResult has all the data we need
                    window.refreshAfterMarkAsPaid();
                };
            } else {
                document.getElementById("btnPromptOkayMarkAsPaid").style.visibility = "visible";
                document.getElementById("btnPromptOkayMarkAsPaid").style.display = "initial";
                document.getElementById("loadingProcess").style.visibility = "hidden";
                document.getElementById("SuccessStatus").style.visibility = "visible";
                document.getElementById("SuccessStatus").style.display = "initial";
                
                // Use stored descriptive error message instead of generic "error occured"
                var errorMessage = window.lastMarkAsPaidError || "An error occurred while processing the payment";
                document.getElementById('SuccessStatus').innerHTML = window.getErrorDisplayHtml(errorMessage);

                document.getElementById("btnPromptOkayMarkAsPaid").onclick = function(e) {
                    dialogProgress.hide();
                };
            }
        }
    }
};

window.promptEmailAddressValidation = function(selectedInvoices, invoicewitherrors) {
    var contactWithNoEmail = "";
    document.getElementById("contactWithNoEmail").innerHTML = "";
    var list = document.getElementById("contactWithNoEmail");
    for (var i = 0; i < invoicewitherrors.length; i++) {
        var li = document.createElement('li');
        li.innerText = invoicewitherrors[i].error;
        list.appendChild(li);
    }

    window.NumOfInvoice = selectedInvoices.length;
    window.NumOfIssues = invoicewitherrors.length;

    var dialog = document.getElementById("promptDialogMarkAsPaid").ej2_instances[0];
    document.getElementById("promptDialogMarkAsPaid").style.maxHeight = "";
    var titleFormat = "Mark as Paid";
    document.getElementById("fileFormat2").innerHTML = titleFormat;
    document.getElementById("promptMarkAsIssue").innerHTML = "You are about to generate a BatchPay file containing " + window.NumOfInvoice + " bills. The file however contains " + window.NumOfIssues + " errors.";

    var dlgHeader = document.getElementById("promptDialogMarkAsPaid_dialog-header");
    var dlgContent = document.getElementById("promptDialogMarkAsPaid_dialog-content");

    dlgHeader.classList.add("error-dlg-header-template");
    dlgContent.classList.add("dlg-content-template");

    dialog.show();

    document.getElementById("btnShowMarkAsPaidIssues").onclick = function(e) {
        var blob = new Blob([contactWithNoEmail], { type: "text/plain;charset=utf-8" });
        var dialogMarkAsPaidViewErrorList = document.getElementById("dialogMarkAsPaidViewErrorList").ej2_instances[0];

        var dlgHeader2 = document.getElementById("dialogMarkAsPaidViewErrorList_dialog-header");
        var dlgContent2 = document.getElementById("dialogMarkAsPaidViewErrorList_dialog-content");

        dlgHeader2.classList.add("error-dlg-header-template");
        dlgContent2.classList.add("dlg-content-template");

        dialogMarkAsPaidViewErrorList.show();
    };
    document.getElementById("btnMarkAsPaidViewErrorList").onclick = function(e) {
        var dialogMarkAsPaidViewErrorList = document.getElementById("dialogMarkAsPaidViewErrorList").ej2_instances[0];
        dialogMarkAsPaidViewErrorList.hide();
    };
};

window.updateGridSelectionAndData = function() {
    const gridInstance = document.getElementById('Grid').ej2_instances[0];
    document.getElementById("spinnerGlobal").style.visibility = "visible";

    if (gridInstance) {
        const dataSource = gridInstance.dataSource;
        const currentPage = gridInstance.pageSettings.currentPage;
        const pageSize = gridInstance.pageSettings.pageSize;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        const currentPageData = dataSource.slice(startIndex, endIndex);
        const idToIndexMap = new Map(currentPageData.map(function(item, index) { return [item.invoiceID, startIndex + index]; }));

        const selectedIndexes = [];
        const storedData = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_DATA)) || [];

        storedData.forEach(function(data) {
            const index = idToIndexMap.get(data.invoiceID);
            if (index !== undefined) {
                selectedIndexes.push(index);
            }
        });

        gridInstance.clearSelection();
        gridInstance.selectRows(selectedIndexes, true);
        gridInstance.refresh();

        setTimeout(function() {
            document.getElementById("spinnerGlobal").style.visibility = "hidden";
            gridInstance.hideSpinner();
        }, 500);
    } else {
        console.error("Grid instance is not initialized.");
        document.getElementById("spinnerGlobal").style.visibility = "hidden";
    }
};

window.DataSourceChanged = function() {
    var grid = document.getElementById("Grid").ej2_instances[0];
    grid.showSpinner();
};

window.DataStateChange = function() {
    var grid = document.getElementById("Grid").ej2_instances[0];
    grid.showSpinner();
};

window.modalCreated = function() {
    window.dialogObj = this;
    window.dialogObj.hide();
};

window.onKeyupMessageBatchReferenceNoMp = function(e) {
    var len = 0;
    var maxchar = 12;

    $('#txtBatchReferenceNoMp').keyup(function () {
        len = this.value.length;
        if (len > maxchar) {
            return false;
        }
        else if (len == maxchar) {
            document.getElementById("counter").style.visibility = "visible";
            document.getElementById("counter").style.display = "Block";
        }
        else if (len < maxchar) {
            document.getElementById("counter").style.display = "None";
            document.getElementById("counter").style.visibility = "hidden";
        }
    });
    if (e.value == "")
        document.getElementById("messageBatchReferenceNoMp").style.display = "Block";
    else
        document.getElementById("messageBatchReferenceNoMp").style.display = "None";
        document.getElementById("counter").style.display = "None";
        document.getElementById("counter").style.visibility = "hidden";
};

window.onKeyupBacsServiceUserNoMp = function(e) {
    if (e.value == "")
        document.getElementById("messageBacsServiceUserNoMp").style.display = "Block";
    else
        document.getElementById("messageBacsServiceUserNoMp").style.display = "None";
};

window.onKeyupNarrativeUserChoiceMp = function(e) {
    if (e.value == "")
        document.getElementById("messageNarrativeUserChoiceMp").style.display = "Block";
    else
        document.getElementById("messageNarrativeUserChoiceMp").style.display = "None";
};

// Toolbar click handlers
window.sortClick = function() {
    var el = document.getElementById("SortColumns");
    if (el) el.click();
};

window.clearClick = function() {
    var el = document.getElementById("ClearSortColumns");
    if (el) el.click();
    var existingSelection = JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTED_DATA)) || [];
    localStorage.removeItem(STORAGE_KEYS.SELECTED_DATA_FOR_REMITTANCE);
    if (existingSelection == undefined) {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_DATA);
        localStorage.removeItem(STORAGE_KEYS.SELECTED_DATA_FOR_REMITTANCE);
    } else {
        var subscriptionID = getState('subscriptionID');
        var filteredSelection = existingSelection.filter(function(item) {
            return item.orgId !== undefined && item.orgId !== subscriptionID;
        });
        localStorage.setItem(STORAGE_KEYS.SELECTED_DATA, JSON.stringify(filteredSelection));
        window.location = "/Home/Index/";
    }
};

window.refreshClick = function() {
    var el = document.getElementById("Refresh");
    if (el) el.click();
    window.location = "/Home/Index/";
};

// Simple refresh after Mark as Paid - just close dialog and update grid statuses
// Uses window.markAsPaidResult which already has the payment data for remittances
window.refreshAfterMarkAsPaid = function() {
    console.log("refreshAfterMarkAsPaid called - updating grid without page reload");
    
    try {
        var gridElement = document.getElementById("Grid");
        if (!gridElement || !gridElement.ej2_instances || !gridElement.ej2_instances[0]) {
            console.error("Grid not found");
            return;
        }
        
        var grid = gridElement.ej2_instances[0];
        
        // Build a map of invoice ID → payment data from window.markAsPaidResult
        var paymentsByInvoiceId = {};
        if (window.markAsPaidResult && window.markAsPaidResult.invoices && window.markAsPaidResult.invoices._Payments) {
            window.markAsPaidResult.invoices._Payments.forEach(function(payment) {
                if (payment.invoice && payment.invoice.invoiceID) {
                    paymentsByInvoiceId[payment.invoice.invoiceID] = {
                        amount: payment.amount || 0,
                        paymentID: payment.paymentID
                    };
                }
            });
        }
        
        var paidCount = Object.keys(paymentsByInvoiceId).length;
        console.log("Paid invoices to update:", paidCount);
        
        // Update the status and amounts of paid bills in the grid's data source
        if (paidCount > 0 && grid.dataSource && Array.isArray(grid.dataSource)) {
            grid.dataSource.forEach(function(row) {
                var paymentData = paymentsByInvoiceId[row.invoiceID];
                if (paymentData) {
                    // Update status
                    row.status = "Paid";
                    
                    // Update payment amounts
                    var paidAmount = paymentData.amount;
                    row.amountPaid = (row.amountPaid || 0) + paidAmount;
                    row.amountDue = Math.max(0, (row.amountDue || 0) - paidAmount);
                    // Keep amountDueEditable as the payment amount (paidAmount) for remittance purposes
                    // Do NOT reset it to remaining balance (row.amountDue) as that would show $0 in remittance emails
                    row.amountDueEditable = paidAmount;
                    
                    console.log("Updated invoice " + row.invoiceID + ": paid=" + paidAmount + ", amountDue=" + row.amountDue);
                }
            });
            
            // Clear the selection and refresh the grid
            grid.clearSelection();
            grid.refresh();
            
            console.log("Grid updated - paid bills now show 'Paid' status with updated amounts");
        }
        
        // Enable Step 3 and Step 4 buttons (Send Remittances and Export Report)
        var step3 = document.getElementById("step3");
        var step4 = document.getElementById("step4");
        if (step3 && step3.ej2_instances && step3.ej2_instances[0]) {
            step3.ej2_instances[0].disabled = false;
        }
        if (step4 && step4.ej2_instances && step4.ej2_instances[0]) {
            step4.ej2_instances[0].disabled = false;
        }
        
        // Disable Step 1 and Step 2 buttons
        var step1 = document.getElementById("step1");
        var step2 = document.getElementById("step2");
        if (step1 && step1.ej2_instances && step1.ej2_instances[0]) {
            step1.ej2_instances[0].disabled = true;
        }
        if (step2 && step2.ej2_instances && step2.ej2_instances[0]) {
            step2.ej2_instances[0].disabled = true;
        }
        
        // Enable toolbar buttons for remittances
        try {
            grid.toolbarModule.enableItems(['SendRemittances'], true);
            grid.toolbarModule.enableItems(['ExportBatchPaymentReport'], true);
            grid.toolbarModule.enableItems(['MarkAsPaid'], false);
            grid.toolbarModule.enableItems(['Export'], false);
            grid.toolbarModule.enableItems(['ExportCSV'], false);
            grid.toolbarModule.enableItems(['ExportToBacs'], false);
        } catch (toolbarError) {
            console.log("Could not update toolbar items:", toolbarError);
        }
        
        console.log("Grid refresh complete - ready for remittances");
        
    } catch (error) {
        console.error("Error in refreshAfterMarkAsPaid:", error);
    }
};

// Feedback dialog handler
window.feedbackClick = function() {
    var dialogFeedbackModal = document.getElementById("dialogFeedbackModal");
    if (dialogFeedbackModal && dialogFeedbackModal.ej2_instances) {
        var dialog = dialogFeedbackModal.ej2_instances[0];
        var dlgHeader = document.getElementById("dialogFeedbackModal_dialog-header");
        var dlgContent = document.getElementById("dialogFeedbackModal_dialog-content");
        if (dlgHeader) dlgHeader.classList.add("prompt-dlg-header-template");
        if (dlgContent) dlgContent.classList.add("dlg-content-template");
        dialog.show();
        var feedbackDesc = document.getElementById("feedbackDesc");
        if (feedbackDesc) feedbackDesc.value = "";
    }
};

// Intro.js tour
window.introJssssss = function() {
    if (typeof introJs !== 'undefined') {
        introJs().setOptions({
            exitOnEsc: false,
            autoPosition: true,
            exitOnOverlayClick: false,
            steps: [
                { title: 'Welcome to BatchPay', intro: 'Please follow this quick tour to familiarise yourself with BatchPay\'s core features' },
                { element: document.querySelector('.e-grid .e-rowcell .e-checkbox-wrapper, .e-grid .e-rowcell .e-css.e-checkbox-wrapper'), intro: 'First, select the bills you want to pay by clicking the checkbox on the left of the grid' },
                { element: document.querySelector('.e-grid .e-gridheader'), intro: 'Filter and sort all the bills to easily find just the ones you want to pay' },
                { element: document.querySelector('.export'), intro: 'Once you\'ve selected the bills for your batch payment, click the Export BatchPay File button to download the batch file' },
                { element: document.querySelector('.markaspaid'), intro: 'Once you\'ve generated your BatchPay file and processed the file in your banking portal, click the Mark as Paid button to mark those bills as Paid in Xero' },
                { element: document.querySelector('.remittance'), intro: 'Click the Send Remittances button to email a detailed record of payment to all your contacts' },
                { element: document.querySelector('.batchreport'), intro: 'The final step is to save a record of the batch transaction. Click the Export BatchPay Report to download a detailed report of the batch' }
            ]
        }).start();
    }
};

// Progress load handler (for progress bar theming)
window.progressLoad = function(args) {
    var div = document.getElementsByClassName('progressbar-label-linear');
    var selectedTheme = location.hash.split('/')[1];
    selectedTheme = selectedTheme ? selectedTheme : 'Material';
    args.progressBar.theme = (selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1)).replace(/-dark/i, 'Dark').replace(/contrast/i, 'Contrast');
};

// myFunction - handles page size change from dropdown
window.myFunction = function() {
    var dropdown = document.getElementById("customPager");
    if (!dropdown) return;
    
    var gridEl = document.getElementById("Grid");
    if (!gridEl || !gridEl.ej2_instances) return;
    
    var gridInstance = gridEl.ej2_instances[0];
    var selectedValue = dropdown.value;
    var newPageSize = parseInt(selectedValue, 10);
    
    // Update grid page size
    gridInstance.pageSettings.pageSize = newPageSize;
    
    // Update state
    setState('selectedPageSizes', selectedValue);
    setState('selectedPageSize', newPageSize);
    
    // Show spinners
    gridInstance.showSpinner();
    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) {
        spinnerGlobal.style.visibility = "visible";
    }
    
    // Update selection after changing the page size
    setTimeout(() => {
        if (typeof window.updateGridSelectionAndData === 'function') {
            window.updateGridSelectionAndData();
        }
    }, 100);
};

// Grid toolbar click handler for sending remittances grid
window.GridSendingRemittancesToolbarlClick = function(args) {
    var gridObj = document.getElementById("GridSendingRemittances");
    if (gridObj && gridObj.ej2_instances) {
        var grid = gridObj.ej2_instances[0];
        if (args.item.id === 'CSVExport') {
            grid.excelExport();
        }
    }
};

// Feedback description value (stored globally for feedbackOnBatchPayClick)
window.feedbackDesc = "";

// Keyup handler for feedback description
window.onKeyupfeedbackDescription = function(e) {
    var str = e.value;
    window.feedbackDesc = str.trimLeft();
    if (window.feedbackDesc) {
        var el = document.getElementById("feedbackDesc1");
        if (el) el.style.visibility = "hidden";
    }
};

// Handler for "Let's get started" button click on trial dialog
window.feedbackOnBatchPayClick = function() {
    var trimmedDesc = window.feedbackDesc.trim();
    
    // Get profile fields
    var firstNameEl = document.getElementById("profileFirstName");
    var surnameEl = document.getElementById("profileSurname");
    var jobTitleEl = document.getElementById("profileJobTitle");
    var companyEl = document.getElementById("profileCompany");
    var countryEl = document.getElementById("profileCountry");
    var phoneEl = document.getElementById("profilePhone");
    
    var firstName = firstNameEl ? firstNameEl.value.trim() : "";
    var surname = surnameEl ? surnameEl.value.trim() : "";
    var jobTitle = jobTitleEl ? jobTitleEl.value.trim() : "";
    var company = companyEl ? companyEl.value.trim() : "";
    var country = countryEl ? countryEl.value.trim() : "";
    var phone = phoneEl ? phoneEl.value.trim() : "";
    
    // Validate all mandatory fields
    var isValid = firstName && surname && jobTitle && company && country;
    
    if (isValid) {
        var spinnerEl = document.getElementById("spinnerSubscription");
        var feedbackDesc1El = document.getElementById("feedbackDesc1");
        
        if (spinnerEl) spinnerEl.style.visibility = "visible";
        if (feedbackDesc1El) feedbackDesc1El.style.visibility = "hidden";
        
        // Reset border colors
        if (firstNameEl) firstNameEl.style.borderColor = "#ddd9d9";
        if (surnameEl) surnameEl.style.borderColor = "#ddd9d9";
        if (jobTitleEl) jobTitleEl.style.borderColor = "#ddd9d9";
        if (companyEl) companyEl.style.borderColor = "#ddd9d9";
        if (countryEl) countryEl.style.borderColor = "#ddd9d9";
        
        // Get subscriptionID from expireOrganization global
        var subscriptionID = "";
        if (typeof expireOrganization !== 'undefined' && expireOrganization.length > 0) {
            var dataArray = expireOrganization.split(",");
            dataArray.forEach(function(item) {
                var splitCode = item.split(":");
                subscriptionID = splitCode[2];
            });
        }
        
        // Activate the trial
        var activateAjax = new ej.base.Ajax({
            url: API_ENDPOINTS.ACTIVATE_TRIAL + subscriptionID,
            type: 'POST',
            contentType: 'application/json',
            accept: 'application/json'
        });
        activateAjax.send();
        
        // Send feedback with profile data
        var payload = {
            Description: trimmedDesc,
            FirstName: firstName,
            Surname: surname,
            JobTitle: jobTitle,
            Company: company,
            Country: country,
            Phone: phone
        };
        var feedbackAjax = new ej.base.Ajax({
            url: API_ENDPOINTS.SEND_FEEDBACK,
            type: 'POST',
            data: JSON.stringify(payload),
            contentType: 'application/json',
            accept: 'application/json'
        });
        feedbackAjax.send();
        feedbackAjax.onSuccess = function(data) {
            // Hide the dialog and spinner instead of refreshing
            var spinnerEl = document.getElementById("spinnerSubscription");
            if (spinnerEl) spinnerEl.style.visibility = "hidden";

            var dialogEl = document.getElementById("promptrActivateSubscriptionDialog");
            if (dialogEl && dialogEl.ej2_instances && dialogEl.ej2_instances[0]) {
                dialogEl.ej2_instances[0].hide();
            }
        };
    } else {
        // Highlight empty required fields
        if (firstNameEl && !firstName) firstNameEl.style.borderColor = "#ff6b6b";
        if (surnameEl && !surname) surnameEl.style.borderColor = "#ff6b6b";
        if (jobTitleEl && !jobTitle) jobTitleEl.style.borderColor = "#ff6b6b";
        if (companyEl && !company) companyEl.style.borderColor = "#ff6b6b";
        if (countryEl && !country) countryEl.style.borderColor = "#ff6b6b";
        
        // Show error message
        var feedbackDesc1El = document.getElementById("feedbackDesc1");
        if (feedbackDesc1El) {
            feedbackDesc1El.style.visibility = "visible";
        }
    }
};

// Searching handler for grid
window.searching = function(args) {
    var grid = document.getElementById("Grid");
    if (grid && grid.ej2_instances) {
        grid.ej2_instances[0].search(args.target.value);
        var text = args.target.value;
        var deleteIcon = document.getElementById("deleteIconId");
        var searchIcon = document.getElementById("searchIconId");
        if (deleteIcon && searchIcon) {
            if (text == null || text == "") {
                deleteIcon.style.display = "none";
                searchIcon.style.display = "block";
            } else {
                searchIcon.style.display = "none";
                deleteIcon.style.display = "block";
            }
        }
    }
};

// Check if contact has more than one invoice - used for lodgement reference display
window.checkContactHasMoreThanOneInvoice = function(selectedInvoices) {
    window.showLodgementReference = true;
    var selectedInvoicesGroup = selectedInvoices.reduce(function (acc, obj) {
        var key = obj.contact.contactID;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
    }, {});

    for (var key in selectedInvoicesGroup) {
        if (selectedInvoicesGroup.hasOwnProperty(key)) {
            if (selectedInvoicesGroup[key].length > 1) {
                window.showLodgementReference = true;
                break;
            }
        }
    }
};

// Handle Use BPAY CBA checkbox change
window.onUseBpayChange = function(args) {
    var bpayInfoContainer = document.getElementById("bpayInfoContainer");
    if (bpayInfoContainer) {
        bpayInfoContainer.style.display = "none";
    }
    var apcaIdEl = document.getElementById("apcaId");
    var messageApcaId = document.getElementById("messageapcaId");
    var txtApcaId = document.getElementById("txtapcaId");
    if (args.checked) {
        var chkNab = document.getElementById("chkUseNabBpay");
        if (chkNab && chkNab.ej2_instances && chkNab.ej2_instances[0]) {
            chkNab.ej2_instances[0].checked = false;
        }
        var nabFields = document.getElementById("nabBpayFieldsContainer");
        if (nabFields) nabFields.style.display = "none";

        if (apcaIdEl) { apcaIdEl.style.visibility = "visible"; apcaIdEl.style.display = "initial"; }
        if (messageApcaId) { messageApcaId.style.display = "initial"; messageApcaId.innerHTML = ''; }
        if (txtApcaId) { txtApcaId.style.visibility = "visible"; txtApcaId.style.display = "initial"; }
    } else {
        if (apcaIdEl) { apcaIdEl.style.visibility = "hidden"; apcaIdEl.style.display = "none"; }
        if (messageApcaId) { messageApcaId.style.display = "none"; messageApcaId.style.visibility = "hidden"; }
    }
};

// Handle Use BPAY NAB checkbox change
window.onUseNabBpayChange = function(args) {
    var nabFields = document.getElementById("nabBpayFieldsContainer");
    var apcaIdEl = document.getElementById("apcaId");
    var messageApcaId = document.getElementById("messageapcaId");
    if (args.checked) {
        var chkCba = document.getElementById("chkUseBpay");
        if (chkCba && chkCba.ej2_instances && chkCba.ej2_instances[0]) {
            chkCba.ej2_instances[0].checked = false;
        }
        var bpayInfoContainer = document.getElementById("bpayInfoContainer");
        if (bpayInfoContainer) bpayInfoContainer.style.display = "none";

        if (nabFields) nabFields.style.display = "block";
        if (apcaIdEl) { apcaIdEl.style.visibility = "hidden"; apcaIdEl.style.display = "none"; }
        if (messageApcaId) { messageApcaId.style.display = "none"; messageApcaId.style.visibility = "hidden"; }
    } else {
        if (nabFields) nabFields.style.display = "none";
    }
};

// Check if CBA BPAY export is selected
window.isUseBpayChecked = function() {
    var chkUseBpay = document.getElementById("chkUseBpay");
    if (chkUseBpay && chkUseBpay.ej2_instances && chkUseBpay.ej2_instances[0]) {
        return chkUseBpay.ej2_instances[0].checked;
    }
    return false;
};

// Check if NAB BPAY export is selected
window.isUseNabBpayChecked = function() {
    var chkNab = document.getElementById("chkUseNabBpay");
    if (chkNab && chkNab.ej2_instances && chkNab.ej2_instances[0]) {
        return chkNab.ej2_instances[0].checked;
    }
    return false;
};

console.log('Globals module loaded - window.* assignments complete');
